/**
 * Awaiting-Input Break-Out Predicate (FH-CLASSIFIER-005 + FH-ESCAPE-006)
 *
 * Pure helper that decides whether an incoming user message should BREAK OUT
 * of the current "awaiting-input" loop in the global-tools message handler,
 * cancel the in-flight runtime, and fall through to the orchestrator (which
 * then runs the PR #37 deterministic pre-router first).
 *
 * BACKGROUND
 * ----------
 * The message handler in `useGlobalTools.ts` has two early exits BEFORE the
 * orchestrator runs:
 *
 *   1. "Awaiting input" loop.
 *      Any runtime reporting `isAwaitingInput()` swallows the next user
 *      message and feeds it back via `runtime.resume(...)`.
 *
 *   2. "Stage1 recovery" fallback.
 *      Any runtime with `stage === 'stage1_confirm' | 'stage1_retry'`
 *      force-resumes even if the awaiting-input flag is stale.
 *
 * FH-CLASSIFIER-005 (original scope)
 * ----------------------------------
 * If the user started a legal-doc-creator workflow (e.g. WCAB Pre-Hearing
 * Statement) and then — in the SAME turn the workflow was waiting on a
 * field answer — uploaded 10–20 files and asked for a manifest (explicitly
 * negating the PHS draft), the awaiting-input loop fed the negation
 * message into the legal-doc-creator as a field answer. The deterministic
 * pre-router never got a chance to run because the orchestrator never ran.
 *
 * FH-ESCAPE-006 (2026-05-07 expansion)
 * ------------------------------------
 * Fernando/account VMs reported three interrelated bugs:
 *   (a) Saying "please read the prehearing statement" still fired the WCAB
 *       PHS drafter.
 *   (b) Users who did NOT want to answer a field question got stuck in a
 *       loop — bare-text escapes like "never mind" / "stop" / "different
 *       question" were swallowed as field answers.
 *   (c) Meta-feedback about the PHS tool ("the prehearing statement tool
 *       didn't work when I did X") was parsed as a new draft request.
 *
 * This helper therefore now supports TEXT-ONLY break-out conditions — no
 * attachments required — for:
 *
 *   D) Explicit abort/re-route phrases (cancel / stop / never mind / go back
 *      / start over / "different question" / Spanish equivalents).
 *   E) Meta-feedback about the tool itself ("the PHS generator didn't work",
 *      "feedback: the tool created a new doc instead of reading mine",
 *      "stuck in a loop").
 *   F) Clearly different intents that have no business being a field answer
 *      (translate / research / "read/summarize/show me/look at THIS
 *      document" / "what is / how do I / tell me about" / "define X").
 *
 * The attachment-gated conditions (A/B/C) are preserved as-is; only the
 * text-only paths are new.
 *
 * NON-REGRESSION
 * --------------
 * The predicate is intentionally narrow. It does NOT fire for:
 *   - Single-file continuations ("here's one more PDF + employer address").
 *   - Normal field answers of any length with no attachments and no
 *     explicit abort / meta / different-intent phrasing.
 *   - Street names containing "Stop" (e.g. "123 Stop Street").
 *   - Affirmative field continuations in Spanish that happen to contain
 *     the word "translate" is handled via the narrower phrase patterns
 *     rather than a bare \btranslate\b match.
 */

import {
  MANIFEST_KEYWORDS,
  LEGAL_DOC_NEGATION_PATTERNS,
  MANIFEST_PREROUTE_MIN_FILES,
  NEGATION_MANIFEST_MIN_FILES,
  findManifestKeyword,
  findLegalDocNegation,
} from '../manifestIntentTables';

// Re-export so any downstream caller can read the same tables via the helper
// module (avoids importing from the orchestrator directly).
export {
  MANIFEST_KEYWORDS,
  LEGAL_DOC_NEGATION_PATTERNS,
  findManifestKeyword,
  findLegalDocNegation,
};

// ============================================================================
// Skip-clarification sentinel (2026-05-28, Justin: avatar skip-restart bug)
// ============================================================================
//
// When a deep-research workflow is parked at stage1_confirm (clarifying-
// question card showing), the avatar (or a button) can request "skip the
// questions, run the research now". That request MUST resume the parked
// runtime with empty clarifications — it must never break out to the
// orchestrator and start a fresh workflow.
//
// The robust signal is a clean, trigger-word-free sentinel token embedded in
// the submitted text. We recognize it case-insensitively and tolerate
// surrounding brackets/whitespace. Both the break-out predicate (here) and
// the deep-research resume path (deepResearch.ts) key off this single helper
// so the two layers can never disagree.
//
// Canonical token: SKIP_CLARIFICATION  (also matches legacy free-text
// "skip clarification" / "skip the clarifying questions" / bare "skip").
export const SKIP_CLARIFICATION_SENTINEL = '[[SKIP_CLARIFICATION]]';

const SKIP_CLARIFICATION_PATTERNS: RegExp[] = [
  /\[\[\s*skip_clarification\s*\]\]/i,
  /\bskip[_\s-]?clarification(?:s)?\b/i,
  /\bskip\s+(?:the\s+)?clarif(?:y|ying)\s+question(?:s)?\b/i,
  /\bskip\s+all\s+(?:the\s+)?question(?:s)?\b/i,
];

/**
 * True when the message is a request to skip clarifying questions and proceed
 * straight to deep research. Trigger-word-free by design: it must NOT depend
 * on (or be confused by) the phrase "deep research" appearing in the text.
 */
export function isSkipClarificationSentinel(message: string | undefined | null): boolean {
  const t = (message || '').trim();
  if (!t) return false;
  if (t.toLowerCase() === 'skip') return true;
  return SKIP_CLARIFICATION_PATTERNS.some((rx) => rx.test(t));
}

// ============================================================================
// Condition C: explicit workflow-abort phrasing with attachments (narrow)
// ============================================================================

/**
 * Explicit abort/re-route phrases historically gated on attachments.length >= 1.
 * Kept for back-compat: these trigger Condition C when attachments are present.
 *
 * The text-only analogs live in `TEXT_ONLY_ABORT_PATTERNS` (Condition D).
 */
export const WORKFLOW_ABORT_PATTERNS: RegExp[] = [
  /\bstart\s+over\b/i,
  /\bstart\s+fresh\b/i,
  /\bstart\s+a?\s*new\s+(request|task|workflow)\b/i,
  /\bnew\s+request\b/i,
  /\bnever\s*mind\b/i,
  /\bforget\s+(that|it|this)\b/i,
  /\bcancel\s+(that|this|the\s+(workflow|draft|form|request))\b/i,
  /\babandon\s+(that|this|the\s+(workflow|draft|form|request))\b/i,
  /\bdifferent\s+request\b/i,
  /\binstead,?\s+(please|i\s+want|i\s+need|give\s+me|do)\b/i,
  // Spanish
  /\bempezar\s+de\s+nuevo\b/i,
  /\bolvid(a|alo|ate|e\s+eso)\b/i,
  /\bnueva\s+solicitud\b/i,
];

/**
 * Return the first matching abort pattern, or undefined.
 */
export function findWorkflowAbortPhrase(message: string): RegExp | undefined {
  if (!message) return undefined;
  for (const rx of WORKFLOW_ABORT_PATTERNS) {
    if (rx.test(message)) return rx;
  }
  return undefined;
}

// ============================================================================
// Condition D: text-only explicit abort phrases (FH-ESCAPE-006)
//
// These MUST be safe to fire with zero attachments. Each pattern is anchored
// tightly enough that common field answers (street names, dates, employer
// names, narrative text) do not match. In particular:
//   - Bare /\bstop\b/ is avoided ("123 Stop Street" must not match).
//   - Bare /\bcancel\b/ is avoided at arbitrary offset; we require it to be
//     a standalone word / phrase / or a direct verb + object construction.
//   - Bare /\bquit\b/, /\bexit\b/ are matched only when standalone or as a
//     verb targeting the workflow/flow/form.
// ============================================================================

export const TEXT_ONLY_ABORT_PATTERNS: RegExp[] = [
  // Single-word / single-phrase escapes — message is entirely (or almost
  // entirely) this word. Allows trailing punctuation, leading "ok " etc.
  /^(?:ok(?:ay)?[,.\s]+)?(?:please\s+)?(cancel|stop|quit|exit|nvm|nevermind|never\s*mind)\s*[.!?]?$/i,

  // Spanish single-word escapes (with and without accents)
  /^(?:ok(?:ay)?[,.\s]+)?(?:por\s+favor\s+)?(cancelar|detente|d[eé]tente|det[eé]nlo|parar|salir|olv[ií]d(?:alo|ate|a\s+eso|[aá]te))\s*[.!?]?$/i,

  // "stop/cancel/abandon + this/that/the workflow/tool/form/draft/request"
  /\b(stop|cancel|abandon|kill|end)\s+(this|that|the)\s+(workflow|tool|form|draft|request|process|flow|wizard)\b/i,
  /\bstop\s+(the\s+)?workflow\b/i,
  /\bcancel\s+(the\s+)?(workflow|request|draft|form|process)\b/i,

  // Polite re-route phrasings
  /\bnever\s*mind\b/i,
  /\bnvm\b/i,
  /\bforget\s+(that|it|this|the\s+(last|previous)\s+(message|thing|request))\b/i,
  /\bscratch\s+that\b/i,
  /\bdisregard\s+(that|this|my\s+last)\b/i,

  // Re-route / new-request phrasings
  /\bdifferent\s+(request|question|topic|thing|direction|matter)\b/i,
  /\bstart\s+over\b/i,
  /\bstart\s+fresh\b/i,
  /\bstart\s+a?\s*new\s+(request|task|workflow|question|topic|thread)\b/i,
  /\bnew\s+(request|question|topic)\b/i,
  /\blet\s+me\s+ask\s+(something|a\s+different\s+question)\b/i,
  /\bi\s+(want|would\s+like|need)\s+to\s+ask\s+(something|a\s+different\s+question)\b/i,
  /\bi\s+have\s+(a\s+)?(different|new|other)\s+question\b/i,
  /\bcan\s+we\s+talk\s+about\s+something\s+else\b/i,

  // Get out / exit the workflow
  /\bget\s+(me\s+)?out\s+of\s+(this|that|the)\s+(workflow|flow|form|request|wizard)\b/i,
  /\bexit\s+(this|that|the)\s+(workflow|flow|form|request|wizard)\b/i,
  /\bleave\s+(this|that)\s+(for\s+now|alone)\b/i,
  /\bgo\s+back\b/i,

  // Spanish re-route / new-request
  /\bempezar\s+de\s+nuevo\b/i,
  /\bnueva\s+solicitud\b/i,
  /\bnueva\s+pregunta\b/i,
  /\botra\s+pregunta\b/i,
  /\btengo\s+(una\s+)?pregunta\s+diferente\b/i,
  /\bcancelar\s+(esto|eso|la\s+(solicitud|forma|petici[oó]n|tarea))\b/i,
  /\bolvida\s+(esto|eso)\b/i,
  /\bdejemos\s+esto\s+por\s+ahora\b/i,
];

/** Return the first matching text-only abort pattern, or undefined. */
export function findTextOnlyAbortPhrase(message: string): RegExp | undefined {
  if (!message) return undefined;
  const trimmed = message.trim();
  for (const rx of TEXT_ONLY_ABORT_PATTERNS) {
    if (rx.test(trimmed)) return rx;
  }
  return undefined;
}

// ============================================================================
// Condition E: meta-feedback / tool-commentary phrases (FH-ESCAPE-006)
//
// When the user is talking ABOUT the tool / workflow / generator — bug
// reports, improvement requests, complaints that the wrong thing happened —
// we must not feed their commentary back to the workflow as a field answer.
// Instead we break out so the orchestrator can route to a conversational
// reply or research path.
// ============================================================================

export const META_FEEDBACK_PATTERNS: RegExp[] = [
  // (D8 tail 2026-08-25: the two 80-char-window patterns — feedback-keyword +
  // tool-noun, and tool-noun + failure-verb — moved to
  // AMBIGUOUS_META_PATTERNS below: a genuine field answer mentioning "the
  // problem with the employer's system" used to eject the form.)

  // Generator / tool created a new doc when I didn't ask
  /\b(generator|tool|workflow|drafter|creator)\b[\s\S]{0,60}\b(created|generated|made|produced)\s+(a\s+)?(new\s+)?(document|doc|draft|statement|file)\b/i,

  // "Stuck in a loop" — the diagnostic phrase from the bug report itself
  /\bstuck\s+in\s+a\s+loop\b/i,
  /\binfinite\s+loop\b/i,

  // "I was trying to ... but it did X instead"
  /\bi\s+(was|am)\s+(trying|asking)\s+to\s+\w+[\s\S]{0,80}\b(but|however|instead)\b[\s\S]{0,80}\b(it|you|prest0n|the\s+(tool|workflow|generator))\b/i,

  // Spanish
  /\bretroalimentaci[oó]n\b/i,
  /\bno\s+quer[ií]a\b/i,
  /\bla\s+herramienta\s+(no\s+funcion[oó]|fall[oó]|est[aá]\s+rota)/i,
  /\b(la\s+herramienta|el\s+generador|el\s+flujo|prest0n)\s+(cre[oó]|gener[oó]|hizo)\s+(un|una|otro|otra)\s+(documento|doc|borrador)/i,

  // "when I did X it did Y" meta-feedback about a specific tool
  /\bwhen\s+i\s+(used|tried|asked)\b[\s\S]{0,60}\b(pre[-\s]?hearing|phs|legal\s+doc|document\s+creator|prest0n)\b[\s\S]{0,60}\b(it|the\s+tool)\b/i,
];

/** Return the first matching meta-feedback pattern, or undefined. */
export function findMetaFeedbackPhrase(message: string): RegExp | undefined {
  if (!message) return undefined;
  for (const rx of META_FEEDBACK_PATTERNS) {
    if (rx.test(message)) return rx;
  }
  return undefined;
}

// ============================================================================
// Condition F: clearly-different-intent phrases (FH-ESCAPE-006)
//
// Bare-text messages whose intent is unambiguously something *other than*
// answering the current field question. These are narrow on purpose:
//   - /\btranslate\b/ alone is NOT sufficient (could be part of a narrative).
//     We require "translate (this|the|it|to ...)".
//   - "read/review/summarize/look at/show me" must target a document-like
//     noun to match — prevents innocuous field values from breaking out.
//   - "what is a / how do I / tell me about / explain" patterns fire only
//     on clearly informational phrasing.
// ============================================================================

export const CLEARLY_DIFFERENT_INTENT_PATTERNS: RegExp[] = [
  // Translation requests
  /\btranslate\s+(this|that|these|those|the|it|a|an|my|my\s+\w+|to\s+\w+)\b/i,
  /\btraduce(?:\s+(esto|eso|lo|el|la|al\s+\w+))\b/i,

  // Read / review / summarize / look at / show me THE/THIS/A document
  /\b(read|review|look\s+at|show\s+me|summari[sz]e|summary\s+of|open|have\s+a\s+look\s+at)\s+(the|this|that|these|those|a|an|my)\s+(prehearing\s+statement|pre[-\s]?hearing\s+statement|phs|document|doc|file|statement|pdf|attachment|upload|intake|packet)\b/i,

  // Reverse-order: "the prehearing statement I want you to read" / "read the PHS please"
  /\b(please\s+)?(read|review|look\s+at|summari[sz]e)\s+(it|through\s+it)\b/i,

  // Research / law-research intents
  /\b(research|look\s+up|find)\s+(case\s*law|the\s+law|statutes?|regulations?|cases?\s+on|precedent|\d+[a-z]*\s+\w+|the\s+law\s+on)\b/i,
  // (D8 tail 2026-08-25: the broader /\bresearch\s+\w+/ catch-all moved to
  // AMBIGUOUS_INTENT_PATTERNS below — "research the claimant had done" in a
  // narrative field answer used to eject the form.)
  /\bdeep\s+research\b/i,

  // Informational / explanatory intent (not a command to draft)
  /\bwhat\s+(is|are)\s+(a|an|the)\s+\w+/i,
  /\bwhat\s+does\s+\w+\s+(mean|stand\s+for)\b/i,
  /\btell\s+me\s+(about|what|how|why)\b/i,
  /\b(can|could)\s+you\s+(explain|tell\s+me)\b/i,
  /\bhow\s+do\s+i\s+(write|draft|prepare|file|submit)\s+(a|an)\b/i,
  // Allow multi-word subject between "does" and "work" (e.g. "how does a
  // prehearing statement work").
  /\bhow\s+does\s+(?:\w+\s+){1,6}work\b/i,
  // (D8 tail 2026-08-25: bare /\b(define|definition of)\b/ moved to
  // AMBIGUOUS_INTENT_PATTERNS below.)
  /\bexplain\s+(what|how|the\s+difference)\b/i,
  /\bdo\s+you\s+know\s+what\s+(a|an|the)\b/i,

  // Spanish informational
  /\bqu[eé]\s+es\s+(un|una|el|la)\b/i,
  /\bc[oó]mo\s+(se\s+)?(escribe|redacta|prepara)\s+(un|una)\b/i,
  /\bexplicame\b/i,
  /\bdime\s+(qu[eé]|c[oó]mo|por\s+qu[eé])\b/i,

  // "I want to show you..." — user sharing, not answering a field
  /\bi\s+(want|would\s+like)\s+to\s+show\s+you\b/i,
];

/** Return the first matching clearly-different-intent pattern, or undefined. */
export function findClearlyDifferentIntent(message: string): RegExp | undefined {
  if (!message) return undefined;
  for (const rx of CLEARLY_DIFFERENT_INTENT_PATTERNS) {
    if (rx.test(message)) return rx;
  }
  return undefined;
}

// ============================================================================
// D8 tail (2026-08-25): the AMBIGUOUS band.
//
// These patterns used to be hard break-outs in E/F above, and they were the
// least defensible members of those tables — loose enough that a genuine
// narrative field answer could trip them, and a single false positive
// destroyed the waiting form irreversibly (runtime.cancel() → reset(); the
// legal-doc creator persists nothing). Under the master rule they are now the
// ambiguous band: the message still dispatches to the server as a normal
// turn, but it carries a pending-question hint, the parked workflow SURVIVES,
// and the PRIMARY judges answer-vs-new-topic with the whole thread in view.
// Obvious answers and explicit cancels are untouched (D18: executing the
// primary's prior decision / an unambiguous human decision).
// ============================================================================

export const AMBIGUOUS_INTENT_PATTERNS: RegExp[] = [
  // Broad "research <anything>" — moved from CLEARLY_DIFFERENT_INTENT_PATTERNS.
  /\bresearch\s+(the|a|an|\w+)\b/i,
  // Bare "define" / "definition of" — moved from the same table.
  /\b(define|definition\s+of)\b/i,
];

export const AMBIGUOUS_META_PATTERNS: RegExp[] = [
  // Feedback keyword + tool/workflow noun within 80 chars — moved from
  // META_FEEDBACK_PATTERNS ("improvement(s)"/"improved"/"improving" via prefix).
  /\b(feedback|bug|issue|problem|error|complaint|improv(e|ing|ed|ement|ements))\b[\s\S]{0,80}\b(tool|workflow|generator|creator|agent|prest0n|system|feature|wizard|flow|drafter)\b/i,
  // Tool noun + failure verb within 80 chars — moved from the same table.
  /\b(tool|workflow|generator|creator|agent|prest0n|feature|wizard|flow|drafter)\b[\s\S]{0,80}(?:didn[’']?t\s+work|did\s+not\s+work|does(?:\s*n[’']?t|\s+not)\s+work|not\s+working|is\s+broken|broken|failed|wrong|buggy|bugged|has\s+a\s+bug|created\s+a\s+new|made\s+a\s+new|generated\s+a\s+new|kept\s+asking|keeps\s+asking)/i,
];

/** Return the first matching ambiguous-band pattern, or undefined. */
export function findAmbiguousPhrase(message: string): { rx: RegExp; kind: 'ambiguous-intent' | 'ambiguous-meta' } | undefined {
  if (!message) return undefined;
  for (const rx of AMBIGUOUS_INTENT_PATTERNS) {
    if (rx.test(message)) return { rx, kind: 'ambiguous-intent' };
  }
  for (const rx of AMBIGUOUS_META_PATTERNS) {
    if (rx.test(message)) return { rx, kind: 'ambiguous-meta' };
  }
  return undefined;
}

// ============================================================================
// Condition G: Deep-Research refinement-after-stage1_confirm (T10, 2026-05-15)
//
// Slack thread C0AM8U804KZ/1778481193.723179 — Malaika QA T10 retest.
//
// When the user types a refinement-of-the-prior-answer phrase ("give me a
// deeper version of your last answer", "expand on that", "amplía eso",
// etc.) while the deep-research runtime is parked at stage1_confirm (the
// clarifying-question card with `ir directo a la investigación` /
// `escribir guión de video` buttons), the awaiting-input loop force-resumes
// the runtime — feeding the refinement phrase in as a clarifying-question
// answer and triggering a full Stage 2 deep-research run. The orchestrator
// never gets a chance to apply PR #148's refinement-after-deep-research
// guard because the message never reaches the orchestrator.
//
// This break-out condition cancels the parked runtime so the message falls
// through to the orchestrator, which then routes to a conversational
// continuation reply per PR #148.
//
// SCOPE — toolId-gated to 'deep-research' only. "Deeper" / "more detail"
// phrasing has legitimate field-answer uses in other workflows (e.g.
// translate "more thorough" preference), so we intentionally do NOT fire
// this for legal-doc-creator, translate, or other workflows.
// ============================================================================

export const DEEP_RESEARCH_REFINEMENT_PATTERNS: RegExp[] = [
  // English refinement phrasing — mirrors
  // functions/src/prest0nSession/deepResearchRefinementGuard.ts and the
  // orchestrator-side guard from PR #148 so all three layers stay in sync.
  /\b(?:deeper|deep[\s-]?dive|expand(?:\s+on)?|elaborate|go\s+deeper|more\s+(?:detail|depth|in[\s-]?depth|thorough|comprehensive)|polish|refine|continue)\b/i,

  // "version / take / pass of your last answer / response / memo / output"
  /\b(?:version|take|pass)\s+(?:of\s+)?(?:your\s+)?(?:last|previous|prior)\s+(?:answer|response|memo|output|reply)\b/i,

  // Spanish refinement phrasing
  /\b(?:m[aá]s\s+(?:profund[oa]|detallad[oa]|completo)|profundiza|ampl[ií]a|extiende|elabora|contin[uú]a)\b/i,
];

/** Return the first matching deep-research refinement pattern, or undefined. */
export function findDeepResearchRefinementPhrase(
  message: string
): RegExp | undefined {
  if (!message) return undefined;
  for (const rx of DEEP_RESEARCH_REFINEMENT_PATTERNS) {
    if (rx.test(message)) return rx;
  }
  return undefined;
}

// ============================================================================
// Thresholds (re-exported so tests and callers can tune without forking)
// ============================================================================

export {
  MANIFEST_PREROUTE_MIN_FILES,
  NEGATION_MANIFEST_MIN_FILES,
};

/**
 * Minimum attachment count for Condition C. Any message with an explicit
 * attachment-gated abort phrase AND at least one attachment is treated as a
 * re-route.
 *
 * Note: text-only abort phrases (Condition D) do NOT use this threshold —
 * they fire even with zero attachments per FH-ESCAPE-006.
 */
export const ABORT_BREAKOUT_MIN_FILES = 1;

// ============================================================================
// Types
// ============================================================================

export type BreakOutCondition =
  | 'keyword'        // A: many files + manifest intent
  | 'negation'       // B: files + legal-doc negation
  | 'abort'          // C: files + attachment-gated abort
  | 'text-abort'     // D: text-only abort / re-route phrase (new)
  | 'meta-feedback'  // E: commentary about the tool itself (new)
  | 'different-intent' // F: bare text with clearly different intent (new)
  | 'deep-research-refinement' // G: refinement phrase during deep-research stage1_confirm (T10)
  | 'ambiguous-intent'  // D8 tail: loose intent phrasing — dispatch WITH the pending-question hint, park the form
  | 'ambiguous-meta';   // D8 tail: loose meta phrasing — same handling

/** D8 tail (2026-08-25): three-valued verdict.
 *  'answer'    → the message IS the field answer (fast client path, D18).
 *  'break-out' → explicit cancel/re-route — cancel the runtime, dispatch.
 *  'ambiguous' → dispatch to the server WITH the pending-question hint and
 *                PARK the waiting form (never cancel it) — the PRIMARY
 *                judges answer-vs-new-topic with the whole thread. */
export type BreakOutVerdict = 'answer' | 'break-out' | 'ambiguous';

export interface BreakOutDecision {
  /** True when the message dispatches to the orchestrator instead of being
   *  consumed as the answer (break-out AND ambiguous both dispatch); the
   *  cancel-vs-park choice rides `verdict`. */
  breakOut: boolean;
  verdict: BreakOutVerdict;
  /** Which condition matched. Only set when breakOut === true. */
  condition?: BreakOutCondition;
  /** The matched token/phrase for logging. */
  matched?: string;
  /** The tool id for logging. */
  toolId?: string;
  /** Human-readable reason for logging. */
  reason?: string;
}

export interface BreakOutOptions {
  minKeywordFiles?: number;
  minNegationFiles?: number;
  minAbortFiles?: number;
  /**
   * Disable text-only (zero-attachment) break-outs. Defaults to false.
   * Exposed so the tenantConfig / feature-flag path can toggle without
   * forking the helper if we ever need a kill-switch.
   */
  disableTextOnlyBreakOut?: boolean;
}

// ============================================================================
// Predicate (pure)
// ============================================================================

/**
 * Returns a BreakOutDecision for the given incoming message + attachments.
 * Pure function — safe to unit-test without mocking React hooks.
 *
 * The predicate is INTENDED to be checked BEFORE calling runtime.resume() so
 * that a manifest/negation/abort/meta-feedback/different-intent message can
 * short-circuit the awaiting-input loop and fall through to the orchestrator.
 *
 * Evaluation order:
 *   1. Text-only abort (D)      — fires with zero attachments.
 *   2. Meta-feedback (E)        — fires with zero attachments.
 *   3. Clearly-different intent (F) — fires with zero attachments.
 *   4. Deep-research refinement (G) — toolId-gated to 'deep-research'.
 *   5. Attachment-gated conditions (A / B / C) — existing behavior.
 */
export function shouldBreakOutOfAwaitingInput(
  message: string | undefined | null,
  attachments: File[] | undefined | null,
  toolId?: string,
  options: BreakOutOptions = {}
): BreakOutDecision {
  const text = (message || '').trim();
  const count = attachments?.length ?? 0;

  if (!text) {
    return { breakOut: false, verdict: 'answer' };
  }

  // --------------------------------------------------------------------------
  // Skip-clarification sentinel (2026-05-28, Justin: avatar skip-restart bug).
  //
  // When the avatar submits a "skip the clarifying questions" command into a
  // deep-research thread parked at stage1_confirm, the submission MUST resume
  // the parked runtime (proceed to research with no clarifications) — it must
  // NOT break out to the orchestrator. The prior skip wording was a full
  // sentence containing the literal phrase "Deep Research", which matched the
  // CLEARLY_DIFFERENT_INTENT "deep research" pattern below, broke out of the
  // awaiting-input runtime, fell through to the orchestrator, and started a
  // BRAND-NEW deep-research workflow from scratch (the user saw it "restart").
  //
  // Any message carrying the clean skip sentinel is recognized here and is
  // NEVER a break-out, regardless of any trigger words around it. The
  // deep-research resume path (deepResearch.ts) treats the same sentinel as a
  // skip and proceeds with empty clarifications.
  if (isSkipClarificationSentinel(text)) {
    return { breakOut: false, verdict: 'answer' };
  }

  const minKeyword = options.minKeywordFiles ?? MANIFEST_PREROUTE_MIN_FILES;
  const minNegation = options.minNegationFiles ?? NEGATION_MANIFEST_MIN_FILES;
  const minAbort = options.minAbortFiles ?? ABORT_BREAKOUT_MIN_FILES;

  // --------------------------------------------------------------------------
  // Condition G: Deep-research refinement during stage1_confirm.
  //
  // Toolid-gated: only fires when the awaiting-input runtime is the
  // deep-research workflow. Evaluated FIRST among text-only conditions so a
  // "deeper version" phrasing can never be misclassified as field-answer
  // / abort / different-intent in a deep-research context.
  // --------------------------------------------------------------------------
  if (!options.disableTextOnlyBreakOut && toolId === 'deep-research') {
    const refinementRx = findDeepResearchRefinementPhrase(text);
    if (refinementRx) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'deep-research-refinement',
        matched: refinementRx.source,
        toolId,
        reason: `deep-research refinement phrase (${refinementRx.source}) detected during stage1_confirm — re-routing through orchestrator so PR #148 refinement-after-deep-research guard fires`,
      };
    }
  }

  // --------------------------------------------------------------------------
  // Text-only conditions (D / E / F) — fire regardless of attachment count.
  // --------------------------------------------------------------------------
  if (!options.disableTextOnlyBreakOut) {
    const abortRx = findTextOnlyAbortPhrase(text);
    if (abortRx) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'text-abort',
        matched: abortRx.source,
        toolId,
        reason: `explicit text-only abort/re-route phrase (${abortRx.source}) detected${count ? ` (attachments=${count})` : ''}`,
      };
    }

    const metaRx = findMetaFeedbackPhrase(text);
    if (metaRx) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'meta-feedback',
        matched: metaRx.source,
        toolId,
        reason: `meta-feedback / tool-commentary phrase (${metaRx.source}) detected — not a field answer`,
      };
    }

    const diffRx = findClearlyDifferentIntent(text);
    if (diffRx) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'different-intent',
        matched: diffRx.source,
        toolId,
        reason: `clearly-different-intent phrase (${diffRx.source}) detected — user is not answering the field question`,
      };
    }

    // D8 tail: the AMBIGUOUS band — dispatch with the pending-question hint,
    // PARK the form (the call site must not cancel), the primary decides.
    const ambiguous = findAmbiguousPhrase(text);
    if (ambiguous) {
      return {
        breakOut: true,
        verdict: 'ambiguous',
        condition: ambiguous.kind,
        matched: ambiguous.rx.source,
        toolId,
        reason: `ambiguous phrasing (${ambiguous.rx.source}) — dispatching to the primary with a pending-question hint; the waiting form is parked, not cancelled`,
      };
    }
  }

  // --------------------------------------------------------------------------
  // Attachment-gated conditions (A / B / C).
  // --------------------------------------------------------------------------
  if (count === 0) {
    return { breakOut: false, verdict: 'answer' };
  }

  // Condition A: many files + manifest-intent keyword
  if (count >= minKeyword) {
    const matched = findManifestKeyword(text);
    if (matched) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'keyword',
        matched,
        toolId,
        reason: `${count} files attached + manifest-intent keyword "${matched}" detected`,
      };
    }
  }

  // Condition B: multi-file upload + explicit legal-doc negation
  if (count >= minNegation) {
    const matchedRx = findLegalDocNegation(text);
    if (matchedRx) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'negation',
        matched: matchedRx.source,
        toolId,
        reason: `${count} files attached + explicit legal-doc negation (${matchedRx.source}) detected`,
      };
    }
  }

  // Condition C: any attachment + attachment-gated abort phrasing
  if (count >= minAbort) {
    const matchedAbort = findWorkflowAbortPhrase(text);
    if (matchedAbort) {
      return {
        breakOut: true,
        verdict: 'break-out',
        condition: 'abort',
        matched: matchedAbort.source,
        toolId,
        reason: `${count} files attached + attachment-gated abort phrase (${matchedAbort.source}) detected`,
      };
    }
  }

  return { breakOut: false, verdict: 'answer' };
}
